import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Box,
  TextField,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";

import { uploadHeroImage, buildHeroImageUrl } from "../../api/heroSection";

export type HeroSectionForm = {
  id?: string;
  heading?: string;
  subheading?: string;
  description?: string;
  leftImage?: string;
  rightImage?: string;
};

type Props = {
  open: boolean;
  hero: HeroSectionForm | null;
  onClose: () => void;
  onSave: (data: HeroSectionForm) => Promise<void>;
};

const HeroSectionFormDialog: React.FC<Props> = ({
  open,
  hero,
  onClose,
  onSave,
}) => {
  const [leftFile, setLeftFile] = useState<File | null>(null);
  const [rightFile, setRightFile] = useState<File | null>(null);
  const [form, setForm] = useState<HeroSectionForm>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hero && open) {
      setForm(hero);
      setLeftFile(null);
      setRightFile(null);
      setError(null);
    }
  }, [hero, open]);

  const previewLeft = leftFile
    ? URL.createObjectURL(leftFile)
    : buildHeroImageUrl(form.leftImage || "");

  const previewRight = rightFile
    ? URL.createObjectURL(rightFile)
    : buildHeroImageUrl(form.rightImage || "");

  const pick =
    (side: "left" | "right") => (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f || !f.type.startsWith("image/")) return;
      side === "left" ? setLeftFile(f) : setRightFile(f);
      e.target.value = "";
    };

  const save = async () => {
    setSaving(true);
    setError(null);

    try {
      let leftImage = form.leftImage || "";
      let rightImage = form.rightImage || "";

      if (leftFile) leftImage = await uploadHeroImage(leftFile);
      if (rightFile) rightImage = await uploadHeroImage(rightFile);

      await onSave({
        heading: form.heading,
        subheading: form.subheading,
        description: form.description,
        leftImage,
        rightImage,
      });

      onClose();
    } catch (e: any) {
      setError(e.message || "Failed to save hero section");
    } finally {
      setSaving(false);
    }
  };

  if (!hero) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Hero Section</DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Heading"
            value={form.heading || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, heading: e.target.value }))
            }
            fullWidth
          />

          <TextField
            label="Subheading"
            value={form.subheading || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, subheading: e.target.value }))
            }
            fullWidth
          />

          <TextField
            label="Description"
            multiline
            minRows={2}
            value={form.description || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
            fullWidth
          />
          {/* IMAGES: side-by-side on desktop, stacked on mobile */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-start"
          >
            {/* LEFT */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600} mb={1}>
                Left Image
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  maxWidth: 220,
                  aspectRatio: "3 / 4",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.12)",
                  bgcolor: "#fafafa",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {previewLeft ? (
                  <Box
                    component="img"
                    loading="lazy"
                    decoding="async"
                    src={previewLeft}
                    alt="Left hero"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Typography color="text.secondary">No image</Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} mt={1.25}>
                <Button component="label" startIcon={<AddPhotoAlternateIcon />}>
                  Choose
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={pick("left")}
                  />
                </Button>

                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={!leftFile}
                  onClick={() => setLeftFile(null)}
                >
                  Remove
                </Button>
              </Stack>
            </Box>

            {/* RIGHT */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600} mb={1}>
                Right Image
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  maxWidth: 220,
                  aspectRatio: "3 / 4",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.12)",
                  bgcolor: "#fafafa",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {previewRight ? (
                  <Box
                    component="img"
                    loading="lazy"
                    decoding="async"
                    src={previewRight}
                    alt="Right hero"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Typography color="text.secondary">No image</Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} mt={1.25}>
                <Button component="label" startIcon={<AddPhotoAlternateIcon />}>
                  Choose
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={pick("right")}
                  />
                </Button>

                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={!rightFile}
                  onClick={() => setRightFile(null)}
                >
                  Remove
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeroSectionFormDialog;
